import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import { useResponsive } from '../hooks/useResponsive';
import { generateSlug } from '../utils/slugGenerator';
import OrganizationList from './organization/OrganizationList';
import OrganizationForm from './organization/OrganizationForm';
import TeamList from './teams/TeamList';
import TeamForm from './teams/TeamForm';
import TeamDeleteModal from './teams/TeamDeleteModal';

const OrganizationManager = ({ profile, organizations, teams, onUpdate }) => {
  const { isDesktop } = useResponsive();
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Organization state
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingOrg, setEditingOrg] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    timezone: 'America/New_York',
    currency: 'USD',
    season: '',
    organization_type: 'select_ball',
  });

  // Team state
  const [showTeamForm, setShowTeamForm] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [teamFormData, setTeamFormData] = useState({
    organization_id: profile?.organization_id || '',
    name: '',
    division: '',
    season: '',
    coach_id: '',
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [teamToDelete, setTeamToDelete] = useState(null);
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [localTeams, setLocalTeams] = useState(teams || []);

  const isSuperAdmin = profile?.role === 'super_admin';

  // Sync teams prop to localTeams
  useEffect(() => {
    setLocalTeams(teams || []);
  }, [teams]);

  // Load coaches and teams on mount
  useEffect(() => {
    if (profile) {
      loadTeamsWithCoaches();
      if (profile.organization_id) {
        loadCoaches();
      }
    }
  }, [profile?.organization_id, profile?.role]);

  const loadCoaches = async () => {
    if (profile?.organization_id) {
      await loadCoachesForOrganization(profile.organization_id);
    }
  };

  const loadCoachesForOrganization = async (organizationId) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, full_name, email')
        .eq('organization_id', organizationId)
        .eq('role', 'coach')
        .order('full_name');

      if (error) throw error;
      setCoaches(data || []);
    } catch (err) {
      console.error('Error loading coaches:', err);
    }
  };

  const loadTeamsWithCoaches = async () => {
    try {
      let query = supabase.from('teams').select(`
          *,
          organizations (name),
          team_coaches (
            coach_id,
            role,
            profiles (
              full_name
            )
          )
        `);

      if (!isSuperAdmin && profile?.organization_id) {
        query = query.eq('organization_id', profile.organization_id);
      }

      const { data: teamsData, error } = await query.order('name');

      if (error) throw error;

      const teamsWithCoaches = (teamsData || []).map((team) => ({
        ...team,
        head_coach:
          team.team_coaches?.find((tc) => tc.role === 'head_coach')?.profiles?.full_name ||
          'No coach',
      }));

      setLocalTeams(teamsWithCoaches);
    } catch (err) {
      console.error('Error loading teams:', err);
    }
  };

  // Organization handlers
  const handleNameChange = (e) => {
    const name = e.target.value;
    setFormData({
      ...formData,
      name,
      slug: editingOrg ? formData.slug : generateSlug(name),
    });
  };

  const validateSlug = async (slug) => {
    try {
      let query = supabase.from('organizations').select('id').eq('slug', slug);

      if (editingOrg?.id) {
        query = query.neq('id', editingOrg.id);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error validating slug:', error);
        return true;
      }

      return !data || data.length === 0;
    } catch (err) {
      console.error('Exception validating slug:', err);
      return true;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const isSlugUnique = await validateSlug(formData.slug);
      if (!isSlugUnique) {
        setError('Slug is already taken. Please choose a different one.');
        return;
      }

      const orgData = {
        name: formData.name.trim(),
        slug: formData.slug.trim(),
        organization_type: formData.organization_type,
        settings: {
          timezone: formData.timezone,
          currency: formData.currency,
          season: formData.season,
        },
      };

      if (editingOrg) {
        const { error: updateError } = await supabase
          .from('organizations')
          .update(orgData)
          .eq('id', editingOrg.id);

        if (updateError) throw updateError;
        setSuccess('Organization updated successfully!');
      } else {
        const { error: insertError } = await supabase.from('organizations').insert(orgData);

        if (insertError) throw insertError;
        setSuccess('Organization created successfully!');
      }

      setShowCreateForm(false);
      setEditingOrg(null);
      onUpdate();

      setFormData({
        name: '',
        slug: '',
        timezone: 'America/New_York',
        currency: 'USD',
        season: '',
        organization_type: 'select_ball',
      });
    } catch (err) {
      console.error('Error saving organization:', err);
      setError(err.message);
    }
  };

  const handleEdit = (org) => {
    setEditingOrg(org);
    setShowCreateForm(true);
    setFormData({
      name: org.name,
      slug: org.slug,
      timezone: org.settings?.timezone || 'America/New_York',
      currency: org.settings?.currency || 'USD',
      season: org.settings?.season || '',
      organization_type: org.organization_type || 'select_ball',
    });
    setError('');
    setSuccess('');
  };

  const handleCancel = () => {
    setShowCreateForm(false);
    setEditingOrg(null);
    setFormData({
      name: '',
      slug: '',
      timezone: 'America/New_York',
      currency: 'USD',
      season: '',
      organization_type: 'select_ball',
    });
    setError('');
    setSuccess('');
  };

  // Team handlers
  const handleCreateTeam = () => {
    setTeamFormData({
      organization_id: profile?.organization_id || '',
      name: '',
      division: '',
      season: '',
      coach_id: '',
    });
    setEditingTeam(null);
    setShowTeamForm(true);
    setError('');
    setSuccess('');
  };

  const handleEditTeam = (team) => {
    const headCoach = team.team_coaches?.find((tc) => tc.role === 'head_coach');

    setTeamFormData({
      organization_id: team.organization_id,
      name: team.name,
      division: team.division || '',
      season: team.season || '',
      coach_id: headCoach?.coach_id || '',
    });
    setEditingTeam(team);
    setShowTeamForm(true);
    setError('');
    setSuccess('');
  };

  const handleTeamSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      let teamId = editingTeam?.id;

      if (editingTeam) {
        const { error: updateError } = await supabase
          .from('teams')
          .update({
            name: teamFormData.name,
            division: teamFormData.division || null,
            season: teamFormData.season || null,
          })
          .eq('id', editingTeam.id);

        if (updateError) throw updateError;
      } else {
        const { data: newTeam, error: createError } = await supabase
          .from('teams')
          .insert({
            organization_id: teamFormData.organization_id,
            name: teamFormData.name,
            division: teamFormData.division || null,
            season: teamFormData.season || null,
          })
          .select()
          .single();

        if (createError) throw createError;
        teamId = newTeam.id;
      }

      if (editingTeam) {
        await supabase.from('team_coaches').delete().eq('team_id', teamId).eq('role', 'head_coach');
      }

      if (teamFormData.coach_id) {
        const { error: coachError } = await supabase.from('team_coaches').insert({
          team_id: teamId,
          coach_id: teamFormData.coach_id,
          role: 'head_coach',
        });

        if (coachError) throw coachError;
      }

      await loadTeamsWithCoaches();

      setSuccess(editingTeam ? 'Team updated successfully' : 'Team created successfully');
      setShowTeamForm(false);
      setEditingTeam(null);
    } catch (err) {
      console.error('Error saving team:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelTeamForm = () => {
    setShowTeamForm(false);
    setEditingTeam(null);
    setTeamFormData({
      organization_id: profile?.organization_id || '',
      name: '',
      division: '',
      season: '',
      coach_id: '',
    });
    setError('');
    setSuccess('');
  };

  const handleDeleteTeamClick = (team) => {
    setTeamToDelete(team);
    setShowDeleteModal(true);
  };

  const handleDeleteTeam = async (confirmText) => {
    if (confirmText !== 'yes, delete team') {
      setError('Please type "yes, delete team" to confirm');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const { error } = await supabase.from('teams').delete().eq('id', teamToDelete.id);

      if (error) throw error;

      await loadTeamsWithCoaches();

      setSuccess(`Team "${teamToDelete.name}" deleted successfully`);
      setShowDeleteModal(false);
      setTeamToDelete(null);
    } catch (err) {
      console.error('Error deleting team:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Constants
  const timezones = [
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'America/Phoenix',
    'America/Anchorage',
    'Pacific/Honolulu',
    'America/Toronto',
    'Europe/London',
  ];

  const currencies = ['USD', 'CAD', 'EUR', 'GBP', 'AUD'];

  const organizationTypes = [
    { value: 'select_ball', label: 'Select Ball' },
    { value: 'little_league', label: 'Little League' },
  ];

  const divisionOptions = {
    little_league: ['Tee Ball', 'A', 'AA', 'AAA', 'Majors', 'Intermediate', 'Juniors', 'Seniors'],
    select_ball: ['8U', '9U', '10U', '11U', '12U', '13U', '14U', '15U', '16U', '17U', '18U'],
  };

  const selectedOrg = organizations?.find((org) => org.id === teamFormData.organization_id);
  const selectedOrgType = selectedOrg?.organization_type || 'select_ball';
  const availableDivisions = divisionOptions[selectedOrgType] || divisionOptions.select_ball;

  // Reload coaches when organization changes in team form
  useEffect(() => {
    if (teamFormData.organization_id && showTeamForm) {
      loadCoachesForOrganization(teamFormData.organization_id);
    }
  }, [teamFormData.organization_id, showTeamForm]);

  const styles = {
    card: {
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      padding: isDesktop ? '30px' : '16px',
      marginBottom: isDesktop ? '20px' : '12px',
    },
    cardHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '20px',
      paddingBottom: '15px',
      borderBottom: '1px solid #eee',
    },
    cardTitle: {
      fontSize: isDesktop ? '20px' : '18px',
      fontWeight: 'bold',
      color: '#333',
      margin: 0,
    },
    button: {
      padding: isDesktop ? '10px 20px' : '14px 20px',
      fontSize: '14px',
      fontWeight: '500',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      transition: 'background-color 0.2s',
      minHeight: '44px',
    },
    primaryButton: {
      backgroundColor: '#007bff',
      color: 'white',
    },
    secondaryButton: {
      backgroundColor: '#6c757d',
      color: 'white',
    },
    form: {
      display: 'grid',
      gridTemplateColumns: isDesktop ? '1fr 1fr' : '1fr',
      gap: '20px',
    },
    formGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
    },
    label: {
      fontSize: '14px',
      fontWeight: '500',
      color: '#333',
    },
    input: {
      padding: '10px',
      fontSize: '14px',
      border: '1px solid #ddd',
      borderRadius: '4px',
      outline: 'none',
    },
    select: {
      padding: '10px',
      fontSize: '14px',
      border: '1px solid #ddd',
      borderRadius: '4px',
      outline: 'none',
      backgroundColor: 'white',
    },
    formActions: {
      gridColumn: '1 / -1',
      display: 'flex',
      gap: '10px',
      justifyContent: 'flex-end',
      marginTop: '10px',
    },
    error: {
      padding: '12px',
      backgroundColor: '#fee',
      color: '#c33',
      borderRadius: '4px',
      fontSize: '14px',
      border: '1px solid #fcc',
      marginBottom: '20px',
    },
    success: {
      padding: '12px',
      backgroundColor: '#efe',
      color: '#3c3',
      borderRadius: '4px',
      fontSize: '14px',
      border: '1px solid #cfc',
      marginBottom: '20px',
    },
    orgList: {
      marginTop: '20px',
    },
    orgItem: {
      padding: isDesktop ? '15px' : '12px',
      borderBottom: '1px solid #eee',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexWrap: isDesktop ? 'nowrap' : 'wrap',
      gap: '10px',
    },
    orgInfo: {
      flex: 1,
    },
    orgName: {
      fontSize: '16px',
      fontWeight: '600',
      color: '#333',
      marginBottom: '5px',
    },
    orgDetails: {
      fontSize: '14px',
      color: '#666',
    },
    sectionHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '20px',
    },
    formContainer: {
      backgroundColor: '#f8f9fa',
      padding: '20px',
      borderRadius: '8px',
      marginBottom: '30px',
    },
    formTitle: {
      fontSize: '18px',
      fontWeight: 'bold',
      marginBottom: '20px',
    },
    teamsContainer: {
      marginTop: '20px',
    },
    orgGroupTitle: {
      fontSize: '18px',
      fontWeight: '600',
      color: '#333',
      marginBottom: '15px',
      paddingBottom: '10px',
      borderBottom: '2px solid #e0e0e0',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      backgroundColor: 'white',
    },
    th: {
      textAlign: 'left',
      padding: '12px',
      backgroundColor: '#f8f9fa',
      fontWeight: '600',
      borderBottom: '2px solid #dee2e6',
    },
    td: {
      padding: '12px',
      borderBottom: '1px solid #dee2e6',
    },
    smallButton: {
      padding: '6px 12px',
      fontSize: '12px',
      fontWeight: '500',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      transition: 'background-color 0.2s',
      minHeight: '32px',
    },
    dangerButton: {
      backgroundColor: '#d32f2f',
      color: 'white',
    },
    cardContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',
    },
    teamCard: {
      backgroundColor: 'white',
      border: '1px solid #e0e0e0',
      borderRadius: '8px',
      padding: '16px',
    },
    teamCardHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '12px',
    },
    teamCardTitle: {
      fontSize: '18px',
      fontWeight: 'bold',
      margin: 0,
    },
    badge: {
      display: 'inline-block',
      padding: '4px 8px',
      backgroundColor: '#e3f2fd',
      color: '#1976d2',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: '600',
    },
    teamCardBody: {
      marginBottom: '12px',
      fontSize: '14px',
    },
    teamCardActions: {
      display: 'flex',
      gap: '8px',
    },
    modalOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
    },
    modal: {
      backgroundColor: 'white',
      borderRadius: '8px',
      padding: '30px',
      maxWidth: '500px',
      width: '90%',
      maxHeight: '90vh',
      overflowY: 'auto',
    },
    modalTitle: {
      fontSize: '24px',
      fontWeight: 'bold',
      marginBottom: '20px',
    },
    modalText: {
      fontSize: '16px',
      marginBottom: '10px',
    },
    modalWarning: {
      fontSize: '14px',
      color: '#d32f2f',
      backgroundColor: '#ffebee',
      padding: '12px',
      borderRadius: '4px',
      marginBottom: '20px',
    },
    modalActions: {
      display: 'flex',
      gap: '10px',
      justifyContent: 'flex-end',
      marginTop: '20px',
    },
  };

  return (
    <div>
      {error && <div style={styles.error}>{error}</div>}
      {success && <div style={styles.success}>{success}</div>}

      {/* Organization Management Card */}
      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <h2 style={styles.cardTitle}>Organization Management</h2>
          {isSuperAdmin && !showCreateForm && (
            <button
              style={{ ...styles.button, ...styles.primaryButton }}
              onClick={() => setShowCreateForm(true)}
            >
              + Create Organization
            </button>
          )}
        </div>

        <OrganizationForm
          show={showCreateForm}
          formData={formData}
          setFormData={setFormData}
          editingOrg={editingOrg}
          timezones={timezones}
          currencies={currencies}
          organizationTypes={organizationTypes}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          onNameChange={handleNameChange}
          styles={styles}
        />

        <OrganizationList
          organizations={organizations}
          isSuperAdmin={isSuperAdmin}
          profile={profile}
          onEdit={handleEdit}
          styles={styles}
        />
      </div>

      {/* Team Management Card */}
      <div style={styles.card}>
        <div style={styles.sectionHeader}>
          <h2 style={styles.cardTitle}>Team Management</h2>
          <button style={{ ...styles.button, ...styles.primaryButton }} onClick={handleCreateTeam}>
            + Create Team
          </button>
        </div>

        <TeamForm
          show={showTeamForm}
          formData={teamFormData}
          setFormData={setTeamFormData}
          editingTeam={editingTeam}
          organizations={organizations}
          coaches={coaches}
          availableDivisions={availableDivisions}
          isSuperAdmin={isSuperAdmin}
          loading={loading}
          onSubmit={handleTeamSubmit}
          onCancel={handleCancelTeamForm}
          styles={styles}
        />

        <TeamList
          teams={localTeams}
          organizations={organizations}
          onEdit={handleEditTeam}
          onDelete={handleDeleteTeamClick}
          styles={styles}
        />
      </div>

      <TeamDeleteModal
        show={showDeleteModal}
        team={teamToDelete}
        onCancel={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteTeam}
        loading={loading}
        error={error}
        styles={styles}
      />
    </div>
  );
};

export default OrganizationManager;
